import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Badge, Button, Checkbox, Hint, ImageThumb, StatusDot, cn, } from '@wit-support/ui';
import { PaybackStrip } from '@wit-support/ui-domain';
import { ChevronDown, ChevronRight, Clock, Layers, LayoutGrid } from 'lucide-react';
import { shortWhen } from './format.js';
import { LaunchOriginBadge } from './components/launch-notice.js';
import { rowStatus } from './tree.js';
import { CPP_TONE_WORD, PAYBACK_SCALE_MAX, PAYBACK_THRESHOLD, TARGET_CPP_MINOR, cppBarPct, cppTone, dayLabel, fmtMoney, perDollarText, pauseDaysView, } from './view.js';
export const COLUMN_DEFS = [
    {
        id: 'select',
        label: 'Выбор строк',
        hideable: false,
        reorderable: false,
        defaultPin: 'left',
        minWidth: 30,
    },
    { id: 'name', label: 'Кампания', hideable: false, defaultPin: 'left', minWidth: 360 },
    { id: 'inMeta', label: 'В Meta', minWidth: 62 },
    { id: 'status', label: 'Статус', minWidth: 120 },
    { id: 'pauseDays', label: 'Дни паузы', minWidth: 94 },
    { id: 'cppD1', label: 'Цена покупки · вчера', minWidth: 64 },
    { id: 'cppD7', label: 'Цена покупки · 7 дн', minWidth: 64 },
    { id: 'cppD30', label: 'Цена покупки · 30 дн', minWidth: 64 },
    { id: 'cppD90', label: 'Цена покупки · 90 дн', minWidth: 64 },
    { id: 'budget', label: 'Бюджет в день', minWidth: 88 },
    { id: 'spendToday', label: 'Расход за день', minWidth: 82 },
    { id: 'purchases', label: 'Покупки за день', minWidth: 62 },
    { id: 'cppToday', label: 'Цена покупки за день', minWidth: 78 },
    { id: 'payback', label: 'Вернётся на 1 $', minWidth: 92 },
];
const MUTED = 'text-[var(--color-text-muted)]';
const dash = _jsx("span", { className: MUTED, children: "\u2014" });
/** За прошлый день у детей цифры есть, только если auto-ads прислал их за этот день (иначе «—»). */
const noDayData = (row, ctx) => row.depth > 0 && ctx.day !== ctx.today && row.spendToday === null;
// ── название ─────────────────────────────────────────────────────────────
function KindIcon({ row }) {
    if (row.kind === 'ad')
        return (_jsx(ImageThumb, { src: row.thumbnailUrl, label: row.name || 'Объявление', className: "!h-7 !w-7 !rounded-[var(--radius-1)]" }));
    const Icon = row.kind === 'campaign' ? Layers : LayoutGrid;
    return (_jsx("span", { title: row.kind === 'campaign' ? 'Кампания' : 'Группа', className: "inline-flex", children: _jsx(Icon, { "aria-hidden": true, className: cn('h-4 w-4 shrink-0', MUTED) }) }));
}
function Chip({ tone = 'neutral', children, title, }) {
    return (_jsx(Badge, { title: title, variant: tone === 'bad'
            ? 'danger'
            : tone === 'info'
                ? 'info'
                : tone === 'warn'
                    ? 'warning'
                    : 'neutral', className: "h-5 whitespace-nowrap px-1.5 py-0 text-[11px]", children: children }));
}
/** Название в одну строку; полный текст (и ID) во всплывающей подсказке — только когда оно не поместилось. */
function TruncatedName({ name, id, className }) {
    const ref = useRef(null);
    const [cut, setCut] = useState(false);
    const measure = () => {
        const el = ref.current;
        if (el)
            setCut(el.scrollWidth > el.clientWidth + 1);
    };
    useLayoutEffect(measure, [name]);
    useEffect(() => {
        const el = ref.current;
        if (!el || typeof ResizeObserver === 'undefined')
            return;
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    return (_jsx("span", { ref: ref, className: cn('min-w-0 truncate', className), title: cut ? `${name} · ID ${id}` : undefined, "data-testid": "row-name", children: name }));
}
export function NameCell({ row, ctx }) {
    const c = row.campaign;
    const indent = row.depth * 20;
    const origin = c?.origin;
    const matched = row.depth === 0 && row.matchedIn && row.matchedIn !== 'campaign'
        ? row.matchedIn === 'adset'
            ? 'найдено в группе'
            : 'найдено в объявлении'
        : null;
    return (_jsxs("div", { className: "flex min-w-0 items-start gap-1.5", style: { paddingLeft: indent }, children: [row.expandable ? (_jsx(Button, { type: "button", size: "icon", variant: "ghost", className: "-my-0.5 h-6 w-6 shrink-0 text-[var(--color-text-muted)]", "aria-label": `${row.expanded ? 'Свернуть' : 'Раскрыть'} «${row.name}»`, "aria-expanded": row.expanded, onClick: () => ctx.onToggleExpand(row), children: row.expanded ? (_jsx(ChevronDown, { "aria-hidden": true, className: "h-4 w-4" })) : (_jsx(ChevronRight, { "aria-hidden": true, className: "h-4 w-4" })) })) : (_jsx("span", { className: "inline-block h-5 w-6 shrink-0", "aria-hidden": true })), _jsxs("div", { className: "flex min-w-0 flex-1 flex-col gap-1", children: [_jsxs("span", { className: "flex min-w-0 items-center gap-2", children: [_jsx(KindIcon, { row: row }), ctx.onOpenCard && row.kind !== 'ad' ? (_jsx(Button, { type: "button", variant: "ghost", size: "sm", className: "h-auto min-w-0 justify-start p-0 text-left text-[var(--color-text-link)] hover:underline", onClick: () => ctx.onOpenCard?.(row), "aria-label": `Открыть карточку: «${row.name}»`, children: _jsx(TruncatedName, { name: row.name || 'Без названия', id: row.id, className: row.depth === 0 ? 'font-medium' : 'text-sm' }) })) : (_jsx(TruncatedName, { name: row.name || 'Без названия', id: row.id, className: row.depth === 0 ? 'font-medium' : 'text-sm' })), _jsx("span", { className: "ml-auto shrink-0", children: ctx.actions(row) })] }), row.groupsLine ||
                        row.launch ||
                        origin?.kind === 'scale' ||
                        origin?.kind === 'duplicate' ||
                        c?.pendingEnable ||
                        matched ? (_jsxs("span", { className: "flex flex-wrap items-center gap-1 pl-6", children: [row.groupsLine && row.depth === 0 ? (_jsx(Chip, { tone: row.groupsLine.bad ? 'bad' : 'neutral', children: row.groupsLine.text })) : null, row.launch ? _jsx(LaunchOriginBadge, { launch: row.launch }) : null, origin?.kind === 'scale' ? _jsx(Chip, { children: "\u29C9 \u043A\u043B\u043E\u043D \u0441\u043A\u0435\u0439\u043B\u0430" }) : null, origin?.kind === 'duplicate' ? (_jsx(Chip, { title: origin.sourceCampaignName ? `Копия «${origin.sourceCampaignName}»` : undefined, children: "\u0434\u0443\u0431\u043B\u044C" })) : null, c?.pendingEnable ? (_jsxs(Chip, { tone: "warn", children: [_jsx(Clock, { "aria-hidden": true, className: "h-3 w-3" }), c.pendingEnable.kind === 'rule'
                                        ? `правило включит ${shortWhen(c.pendingEnable.at) ?? 'позже'}`
                                        : `вкл ${shortWhen(c.pendingEnable.at) ?? 'по таймеру'}`] })) : null, matched ? _jsx(Chip, { tone: "info", children: matched }) : null] })) : null] })] }));
}
// ── ячейки ───────────────────────────────────────────────────────────────
function StatusCell({ row }) {
    const s = rowStatus(row);
    return (_jsxs("span", { className: "flex min-w-0 flex-col gap-0.5", children: [_jsxs("span", { className: "flex items-center gap-1.5 text-sm", children: [_jsx(StatusDot, { variant: s.tone, "aria-label": s.word }), _jsx("span", { className: "truncate", children: s.word })] }), s.sub ? (_jsx("span", { className: cn('truncate pl-3.5 text-xs', s.warn ? 'text-[var(--color-state-warning-fg)]' : MUTED), title: s.sub, children: s.sub })) : null] }));
}
function PauseDaysCell({ row }) {
    if (!row.paused)
        return dash;
    const v = pauseDaysView(row.pauseDays);
    if (!v)
        return dash;
    return (_jsx("span", { title: v.hint, className: cn('whitespace-nowrap text-sm font-semibold', v.tone === 'no' && 'text-[var(--color-state-danger-fg)]', v.tone === 'yes' && 'text-[var(--color-state-success-fg)]', v.tone === 'plain' && cn(MUTED, 'font-normal')), children: v.text }));
}
const TONE_BAR = {
    good: 'bg-[var(--color-state-success-solid)]',
    warn: 'bg-[var(--color-state-warning-solid)]',
    bad: 'bg-[var(--color-state-danger-solid)]',
};
/** Цена покупки: число и полоска против цели $25 (смысл — и словом в подсказке). */
export function CppValue({ minor, currency }) {
    if (minor === null)
        return _jsx("span", { className: cn('text-xs', MUTED), children: "\u043D\u0435\u0442" });
    const tone = cppTone(minor);
    return (_jsxs("span", { className: "flex flex-col items-end gap-0.5", title: `${fmtMoney(minor, currency)} — ${CPP_TONE_WORD[tone]} (цель ${fmtMoney(TARGET_CPP_MINOR, currency, 'whole')})`, children: [_jsx("span", { className: cn('text-sm tabular-nums', tone === 'bad' && 'text-[var(--color-state-danger-fg)]'), children: fmtMoney(minor, currency) }), _jsx("span", { "aria-hidden": true, className: "h-[3px] w-full max-w-[46px] overflow-hidden rounded-full bg-[var(--color-surface-overlay)]", children: _jsx("span", { className: cn('block h-full rounded-full', TONE_BAR[tone]), style: { width: `${cppBarPct(minor)}%` } }) })] }));
}
function BudgetCell({ row }) {
    return (_jsxs("span", { className: "flex flex-col items-end text-sm tabular-nums", children: [_jsx("span", { className: row.depth > 0 && row.budget.main === 'в кампании' ? cn('text-xs', MUTED) : undefined, children: row.budget.main }), row.budget.sub ? _jsx("span", { className: cn('text-xs', MUTED), children: row.budget.sub }) : null] }));
}
function SpendCell({ row, ctx }) {
    if (noDayData(row, ctx) || row.spendToday === null)
        return dash;
    const w = ctx.maxSpend > 0 ? Math.max(3, Math.round((row.spendToday / ctx.maxSpend) * 100)) : 0;
    return (_jsxs("span", { className: "flex flex-col items-end gap-0.5", children: [_jsx("span", { className: "tabular-nums", children: fmtMoney(row.spendToday, ctx.currency) }), _jsx("span", { "aria-hidden": true, className: "h-[3px] w-full max-w-[84px] overflow-hidden rounded-full bg-[var(--color-surface-overlay)]", children: _jsx("span", { className: "block h-full rounded-full bg-[var(--color-brand-bg)] opacity-50", style: { width: `${w}%` } }) })] }));
}
function PurchasesCell({ row, ctx }) {
    if (noDayData(row, ctx) || row.purchasesToday === null)
        return dash;
    const trial = row.trial
        .map((t) => `триал ${fmtMoney(t.trialPriceMinor, ctx.currency, 'whole')} — ${t.count}`)
        .join(', ');
    return (_jsx("span", { className: "tabular-nums", title: trial || undefined, children: row.purchasesToday }));
}
const PAYBACK_NOTE = {
    'no-purchases': 'нет покупок',
    'little-data': 'мало данных',
    new: 'нет данных',
};
function PaybackCell({ row, ctx }) {
    if (row.depth > 0)
        return dash;
    if (ctx.paybackUnavailable)
        return (_jsx(Hint, { content: "\u00AB\u041E\u043A\u0443\u043F\u0430\u0435\u043C\u043E\u0441\u0442\u044C\u00BB \u043D\u0435 \u043E\u0442\u0432\u0435\u0442\u0438\u043B\u0430 \u2014 \u043A\u043E\u043B\u043E\u043D\u043A\u0430 \u0437\u0430\u043F\u043E\u043B\u043D\u0438\u0442\u0441\u044F \u043F\u0440\u0438 \u0441\u043B\u0435\u0434\u0443\u044E\u0449\u0435\u043C \u043E\u0431\u043D\u043E\u0432\u043B\u0435\u043D\u0438\u0438.", children: _jsx("span", { className: cn('text-xs', MUTED), tabIndex: 0, children: "\u043D\u0435\u0442 \u0441\u0432\u044F\u0437\u0438" }) }));
    const p = row.payback;
    if (!p)
        return dash;
    if (p.perDollar === null) {
        return (_jsx("span", { className: cn('text-xs', MUTED), children: p.note ? PAYBACK_NOTE[p.note] : 'мало данных' }));
    }
    const tone = p.confidence === 'low' ? 'none' : p.perDollar >= PAYBACK_THRESHOLD ? 'yes' : 'no';
    const word = p.confidence === 'low'
        ? 'мало покупок, оценка неточная'
        : p.perDollar >= PAYBACK_THRESHOLD
            ? 'выше порога окупаемости'
            : 'ниже порога окупаемости';
    return (_jsxs("span", { className: "flex items-center justify-end gap-2", children: [_jsx(PaybackStrip, { value: p.perDollar, lo: null, hi: null, threshold: PAYBACK_THRESHOLD, max: PAYBACK_SCALE_MAX, tone: tone, ariaLabel: `Вернётся ${perDollarText(p.perDollar)} на 1 $ расхода: ${word}`, className: "!min-w-[52px] !w-[52px]" }), _jsx("span", { className: cn('whitespace-nowrap text-sm font-medium tabular-nums', tone === 'no' && 'text-[var(--color-state-danger-fg)]'), children: perDollarText(p.perDollar) })] }));
}
function SelectCell({ row, ctx }) {
    if (row.depth === 0)
        return (_jsx(Checkbox, { "aria-label": `Выбрать «${row.name}»`, checked: ctx.selected.has(row.id), disabled: !ctx.selected.has(row.id) && ctx.selectLimitReached, onCheckedChange: (v) => ctx.onToggleSelect(row.id, v === true) }));
    // группа и объявление выбираются вместе с кампанией — отмечено «через родителя»
    return ctx.selected.has(row.campaignId) ? (_jsx(Checkbox, { "aria-label": `«${row.name}» выбрано вместе с кампанией`, checked: true, disabled: true, className: "opacity-60" })) : null;
}
export const COLUMN_VIEW = {
    select: { header: null, cell: (r, c) => _jsx(SelectCell, { row: r, ctx: c }) },
    name: { header: 'Кампания', sortKey: 'name', cell: (r, c) => _jsx(NameCell, { row: r, ctx: c }) },
    inMeta: {
        header: 'В Meta',
        sortKey: 'status',
        cell: (r, c) => c.toggle(r),
    },
    status: { header: 'Статус', sortKey: 'status', cell: (r) => _jsx(StatusCell, { row: r }) },
    pauseDays: {
        header: 'Дни паузы',
        sortKey: 'pauseDays',
        cell: (r) => _jsx(PauseDaysCell, { row: r }),
    },
    cppD1: {
        header: 'вчера',
        fullName: 'Цена покупки за вчера',
        sortKey: 'cppD1',
        align: 'right',
        group: 'cpp',
        cell: (r, c) => _jsx(CppValue, { minor: r.cpp?.d1 ?? null, currency: c.currency }),
    },
    cppD7: {
        header: '7 дн',
        fullName: 'Цена покупки за 7 дней',
        sortKey: 'cppD7',
        align: 'right',
        group: 'cpp',
        cell: (r, c) => _jsx(CppValue, { minor: r.cpp?.d7 ?? null, currency: c.currency }),
    },
    cppD30: {
        header: '30 дн',
        fullName: 'Цена покупки за 30 дней',
        sortKey: 'cppD30',
        align: 'right',
        group: 'cpp',
        cell: (r, c) => _jsx(CppValue, { minor: r.cpp?.d30 ?? null, currency: c.currency }),
    },
    cppD90: {
        header: '90 дн',
        fullName: 'Цена покупки за 90 дней',
        sortKey: 'cppD90',
        align: 'right',
        group: 'cpp',
        cell: (r, c) => _jsx(CppValue, { minor: r.cpp?.d90 ?? null, currency: c.currency }),
    },
    budget: {
        header: 'Бюджет в день',
        sortKey: 'budget',
        align: 'right',
        cell: (r) => _jsx(BudgetCell, { row: r }),
    },
    spendToday: {
        header: 'Расход',
        fullName: 'Расход за день',
        sortKey: 'spendToday',
        align: 'right',
        group: 'day',
        cell: (r, c) => _jsx(SpendCell, { row: r, ctx: c }),
    },
    purchases: {
        header: 'Покуп.',
        fullName: 'Покупки за день',
        sortKey: 'purchases',
        align: 'right',
        group: 'day',
        cell: (r, c) => _jsx(PurchasesCell, { row: r, ctx: c }),
    },
    cppToday: {
        header: 'Цена пок.',
        fullName: 'Цена покупки за день',
        sortKey: 'cppToday',
        align: 'right',
        group: 'day',
        cell: (r, c) => noDayData(r, c) ? dash : _jsx(CppValue, { minor: r.cppToday, currency: c.currency }),
    },
    payback: {
        header: 'Вернётся на 1 $',
        sortKey: 'payback',
        align: 'right',
        group: 'day',
        cell: (r, c) => _jsx(PaybackCell, { row: r, ctx: c }),
    },
};
/** Заголовок группы колонок над шапкой. */
export function groupTitle(group, ctx) {
    return group === 'cpp'
        ? ctx.anchorDay
            ? `Цена покупки · окна по ${ctx.anchorDay.slice(8, 10)}.${ctx.anchorDay.slice(5, 7)}`
            : 'Цена покупки · окна'
        : dayLabel(ctx.day, ctx.today);
}
//# sourceMappingURL=columns.js.map